import { gql, useMutation, useQuery } from "@apollo/client";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { FiSave, FiSettings } from "react-icons/fi";

const FEATURE_FLAGS_QUERY = gql`
    query FeatureFlagsQuery {
        featureFlags {
            id
            streamTeamChannelPointRewardEnabled
            streamTeamAcademyEnabled
            streamTeamRaidRpEnabled
            streamTeamSubscriberRpEnabled
            freeRunestoneClaimsEnabled
            academyRunestoneSystemEnabled
        }
    }
`;

const UPDATE_FEATURE_FLAGS_MUTATION = gql`
    mutation UpdateFeatureFlags(
        $streamTeamChannelPointRewardEnabled: Boolean!
        $streamTeamAcademyEnabled: Boolean
        $streamTeamRaidRpEnabled: Boolean
        $streamTeamSubscriberRpEnabled: Boolean
        $freeRunestoneClaimsEnabled: Boolean
        $academyRunestoneSystemEnabled: Boolean
    ) {
        updateFeatureFlags(
            streamTeamChannelPointRewardEnabled: $streamTeamChannelPointRewardEnabled
            streamTeamAcademyEnabled: $streamTeamAcademyEnabled
            streamTeamRaidRpEnabled: $streamTeamRaidRpEnabled
            streamTeamSubscriberRpEnabled: $streamTeamSubscriberRpEnabled
            freeRunestoneClaimsEnabled: $freeRunestoneClaimsEnabled
            academyRunestoneSystemEnabled: $academyRunestoneSystemEnabled
        ) {
            success
            featureFlags {
                id
                streamTeamChannelPointRewardEnabled
                streamTeamAcademyEnabled
                streamTeamRaidRpEnabled
                streamTeamSubscriberRpEnabled
                freeRunestoneClaimsEnabled
                academyRunestoneSystemEnabled
            }
        }
    }
`;

export default function FeatureFlagsPage() {
    const [
        streamTeamChannelPointRewardEnabled,
        setStreamTeamChannelPointRewardEnabled,
    ] = useState(false);
    const [
        savedStreamTeamChannelPointRewardEnabled,
        setSavedStreamTeamChannelPointRewardEnabled,
    ] = useState(false);
    const [streamTeamAcademyEnabled, setStreamTeamAcademyEnabled] =
        useState(false);
    const [savedStreamTeamAcademyEnabled, setSavedStreamTeamAcademyEnabled] =
        useState(false);
    const [streamTeamRaidRpEnabled, setStreamTeamRaidRpEnabled] =
        useState(false);
    const [savedStreamTeamRaidRpEnabled, setSavedStreamTeamRaidRpEnabled] =
        useState(false);
    const [streamTeamSubscriberRpEnabled, setStreamTeamSubscriberRpEnabled] =
        useState(false);
    const [
        savedStreamTeamSubscriberRpEnabled,
        setSavedStreamTeamSubscriberRpEnabled,
    ] = useState(false);
    const [freeRunestoneClaimsEnabled, setFreeRunestoneClaimsEnabled] =
        useState(false);
    const [
        savedFreeRunestoneClaimsEnabled,
        setSavedFreeRunestoneClaimsEnabled,
    ] = useState(false);
    const [
        academyRunestoneSystemEnabled,
        setAcademyRunestoneSystemEnabled,
    ] = useState(false);
    const [
        savedAcademyRunestoneSystemEnabled,
        setSavedAcademyRunestoneSystemEnabled,
    ] = useState(false);
    const [saving, setSaving] = useState(false);

    const { data, loading, error, refetch } = useQuery(FEATURE_FLAGS_QUERY);

    const [updateFeatureFlags] = useMutation(UPDATE_FEATURE_FLAGS_MUTATION, {
        onCompleted: (response) => {
            const savedValue =
                response?.updateFeatureFlags?.featureFlags
                    ?.streamTeamChannelPointRewardEnabled ??
                streamTeamChannelPointRewardEnabled;
            const savedAcademyValue =
                response?.updateFeatureFlags?.featureFlags
                    ?.streamTeamAcademyEnabled ?? streamTeamAcademyEnabled;
            const savedRaidRpValue =
                response?.updateFeatureFlags?.featureFlags
                    ?.streamTeamRaidRpEnabled ?? streamTeamRaidRpEnabled;
            const savedSubscriberRpValue =
                response?.updateFeatureFlags?.featureFlags
                    ?.streamTeamSubscriberRpEnabled ??
                streamTeamSubscriberRpEnabled;
            const savedFreeRunestoneValue =
                response?.updateFeatureFlags?.featureFlags
                    ?.freeRunestoneClaimsEnabled ?? freeRunestoneClaimsEnabled;
            const savedAcademyRunestoneValue =
                response?.updateFeatureFlags?.featureFlags
                    ?.academyRunestoneSystemEnabled ??
                academyRunestoneSystemEnabled;
            setSavedStreamTeamChannelPointRewardEnabled(savedValue);
            setStreamTeamChannelPointRewardEnabled(savedValue);
            setSavedStreamTeamAcademyEnabled(savedAcademyValue);
            setStreamTeamAcademyEnabled(savedAcademyValue);
            setSavedStreamTeamRaidRpEnabled(savedRaidRpValue);
            setStreamTeamRaidRpEnabled(savedRaidRpValue);
            setSavedStreamTeamSubscriberRpEnabled(savedSubscriberRpValue);
            setStreamTeamSubscriberRpEnabled(savedSubscriberRpValue);
            setSavedFreeRunestoneClaimsEnabled(savedFreeRunestoneValue);
            setFreeRunestoneClaimsEnabled(savedFreeRunestoneValue);
            setSavedAcademyRunestoneSystemEnabled(savedAcademyRunestoneValue);
            setAcademyRunestoneSystemEnabled(savedAcademyRunestoneValue);
            setSaving(false);
            refetch();
        },
        onError: () => {
            setSaving(false);
        },
    });

    useEffect(() => {
        const savedValue =
            data?.featureFlags?.streamTeamChannelPointRewardEnabled ?? false;
        const savedAcademyValue =
            data?.featureFlags?.streamTeamAcademyEnabled ?? false;
        const savedRaidRpValue =
            data?.featureFlags?.streamTeamRaidRpEnabled ?? false;
        const savedSubscriberRpValue =
            data?.featureFlags?.streamTeamSubscriberRpEnabled ?? false;
        const savedFreeRunestoneValue =
            data?.featureFlags?.freeRunestoneClaimsEnabled ?? false;
        const savedAcademyRunestoneValue =
            data?.featureFlags?.academyRunestoneSystemEnabled ?? false;
        setSavedStreamTeamChannelPointRewardEnabled(savedValue);
        setStreamTeamChannelPointRewardEnabled(savedValue);
        setSavedStreamTeamAcademyEnabled(savedAcademyValue);
        setStreamTeamAcademyEnabled(savedAcademyValue);
        setSavedStreamTeamRaidRpEnabled(savedRaidRpValue);
        setStreamTeamRaidRpEnabled(savedRaidRpValue);
        setSavedStreamTeamSubscriberRpEnabled(savedSubscriberRpValue);
        setStreamTeamSubscriberRpEnabled(savedSubscriberRpValue);
        setSavedFreeRunestoneClaimsEnabled(savedFreeRunestoneValue);
        setFreeRunestoneClaimsEnabled(savedFreeRunestoneValue);
        setSavedAcademyRunestoneSystemEnabled(savedAcademyRunestoneValue);
        setAcademyRunestoneSystemEnabled(savedAcademyRunestoneValue);
    }, [data]);

    const hasChanges =
        streamTeamChannelPointRewardEnabled !==
            savedStreamTeamChannelPointRewardEnabled ||
        streamTeamAcademyEnabled !== savedStreamTeamAcademyEnabled ||
        streamTeamRaidRpEnabled !== savedStreamTeamRaidRpEnabled ||
        streamTeamSubscriberRpEnabled !== savedStreamTeamSubscriberRpEnabled ||
        freeRunestoneClaimsEnabled !== savedFreeRunestoneClaimsEnabled ||
        academyRunestoneSystemEnabled !== savedAcademyRunestoneSystemEnabled;

    const handleSave = async () => {
        setSaving(true);
        await updateFeatureFlags({
            variables: {
                streamTeamChannelPointRewardEnabled,
                streamTeamAcademyEnabled,
                streamTeamRaidRpEnabled,
                streamTeamSubscriberRpEnabled,
                freeRunestoneClaimsEnabled,
                academyRunestoneSystemEnabled,
            },
        });
    };

    if (loading) {
        return <div className="p-4">Loading feature flags...</div>;
    }

    if (error) {
        return (
            <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-red-800">
                Failed to load feature flags.
            </div>
        );
    }

    return (
        <div className="mx-auto mt-4 w-full px-4">
            <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <div className="mb-4 flex items-center gap-2">
                    <FiSettings className="h-6 w-6 text-slate-700 dark:text-slate-300" />
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                        Feature Flags
                    </h1>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                        <div>
                            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                Stream Team Channel Point Rewards
                            </h2>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                Enables reward controls in My Account and admin
                                reward sync actions.
                            </p>
                        </div>
                        <button
                            onClick={() =>
                                setStreamTeamChannelPointRewardEnabled(
                                    !streamTeamChannelPointRewardEnabled,
                                )
                            }
                            className={clsx(
                                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                                streamTeamChannelPointRewardEnabled
                                    ? "bg-green-600"
                                    : "bg-slate-300 dark:bg-slate-600",
                            )}
                        >
                            <span
                                className={clsx(
                                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                    streamTeamChannelPointRewardEnabled
                                        ? "translate-x-6"
                                        : "translate-x-1",
                                )}
                            />
                        </button>
                    </div>

                    <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                        <div>
                            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                Stream Team Academy Public Page
                            </h2>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                Shows or hides the Academy page and navigation
                                item on the public stream-team site.
                            </p>
                        </div>
                        <button
                            onClick={() =>
                                setStreamTeamAcademyEnabled(
                                    !streamTeamAcademyEnabled,
                                )
                            }
                            className={clsx(
                                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                                streamTeamAcademyEnabled
                                    ? "bg-green-600"
                                    : "bg-slate-300 dark:bg-slate-600",
                            )}
                        >
                            <span
                                className={clsx(
                                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                    streamTeamAcademyEnabled
                                        ? "translate-x-6"
                                        : "translate-x-1",
                                )}
                            />
                        </button>
                    </div>

                    <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                        <div>
                            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                Raid RP
                            </h2>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                Awards RP when a stream team member raids
                                another member's channel.
                            </p>
                        </div>
                        <button
                            onClick={() =>
                                setStreamTeamRaidRpEnabled(
                                    !streamTeamRaidRpEnabled,
                                )
                            }
                            className={clsx(
                                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                                streamTeamRaidRpEnabled
                                    ? "bg-green-600"
                                    : "bg-slate-300 dark:bg-slate-600",
                            )}
                        >
                            <span
                                className={clsx(
                                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                    streamTeamRaidRpEnabled
                                        ? "translate-x-6"
                                        : "translate-x-1",
                                )}
                            />
                        </button>
                    </div>

                    <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                        <div>
                            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                Subscriber RP
                            </h2>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                Awards RP when a player completes 30 days of
                                subscription to a stream team member.
                            </p>
                        </div>
                        <button
                            onClick={() =>
                                setStreamTeamSubscriberRpEnabled(
                                    !streamTeamSubscriberRpEnabled,
                                )
                            }
                            className={clsx(
                                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                                streamTeamSubscriberRpEnabled
                                    ? "bg-green-600"
                                    : "bg-slate-300 dark:bg-slate-600",
                            )}
                        >
                            <span
                                className={clsx(
                                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                    streamTeamSubscriberRpEnabled
                                        ? "translate-x-6"
                                        : "translate-x-1",
                                )}
                            />
                        </button>
                    </div>

                    <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                        <div>
                            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                Free Runestone Claims
                            </h2>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                Allow players to earn and redeem free Runestones
                                based on RP progression.
                            </p>
                        </div>
                        <button
                            onClick={() =>
                                setFreeRunestoneClaimsEnabled(
                                    !freeRunestoneClaimsEnabled,
                                )
                            }
                            className={clsx(
                                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                                freeRunestoneClaimsEnabled
                                    ? "bg-green-600"
                                    : "bg-slate-300 dark:bg-slate-600",
                            )}
                        >
                            <span
                                className={clsx(
                                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                    freeRunestoneClaimsEnabled
                                        ? "translate-x-6"
                                        : "translate-x-1",
                                )}
                            />
                        </button>
                    </div>

                    <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                        <div>
                            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                Academy Runestone System
                            </h2>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                                Enable VRX Academy members to earn and redeem
                                runestones.
                            </p>
                        </div>
                        <button
                            onClick={() =>
                                setAcademyRunestoneSystemEnabled(
                                    !academyRunestoneSystemEnabled,
                                )
                            }
                            className={clsx(
                                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                                academyRunestoneSystemEnabled
                                    ? "bg-green-600"
                                    : "bg-slate-300 dark:bg-slate-600",
                            )}
                        >
                            <span
                                className={clsx(
                                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                    academyRunestoneSystemEnabled
                                        ? "translate-x-6"
                                        : "translate-x-1",
                                )}
                            />
                        </button>
                    </div>

                    <button
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                        className={clsx(
                            "flex items-center gap-2 rounded-md px-4 py-2 font-medium transition-colors",
                            hasChanges && !saving
                                ? "bg-amber-500 text-slate-900 hover:bg-amber-400"
                                : "cursor-not-allowed bg-slate-300 text-slate-500 dark:bg-slate-800 dark:text-slate-500",
                        )}
                    >
                        <FiSave className="h-4 w-4" />
                        {saving ? "Saving..." : "Save Feature Flags"}
                    </button>
                </div>
            </div>
        </div>
    );
}
